<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NativeWhatsappTemplate from 'dashboard/components-next/NewConversation/components/WhatsappTemplate.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TemplatePreview from 'dashboard/components-next/template-preview/TemplatePreview.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import {
  TEMPLATE_PRESET_PURPOSES,
  useTemplateSendPreset,
} from 'custom/composables/useTemplateSendPreset';
import {
  previewVariables,
  templateWithHeaderPreview,
  templateWithMediaPreview,
} from 'custom/helpers/channelTemplatePresets';
import TemplateSendFields from './TemplateSendFields.vue';

const props = defineProps({
  inboxId: { type: Number, required: true },
  template: { type: Object, required: true },
});

const emit = defineEmits(['sendMessage', 'back']);
const { t } = useI18n();
const parser = ref(null);
const fallbackToNative = ref(false);

const {
  currentMediaUrl,
  initializePreset,
  loadingPreset,
  mediaSource,
  processedParams,
  statusLabel,
  statusMessage,
  statusTone,
  uploadedMediaName,
  uploadingMedia,
  uploadMedia: prepareMedia,
} = useTemplateSendPreset({
  parser,
  template: computed(() => props.template),
  inboxId: computed(() => props.inboxId),
  purpose: TEMPLATE_PRESET_PURPOSES.NEW_CONVERSATION,
});

const headerTextFields = computed(() =>
  Object.keys(processedParams.value.header || {}).filter(
    key => !['media_url', 'media_type', 'media_name'].includes(key)
  )
);
const hasEmptyHeaderText = computed(() =>
  headerTextFields.value.some(key => !processedParams.value.header[key])
);
const showValidationError = computed(
  () => !!(parser.value?.v$?.$dirty && parser.value?.v$?.$invalid)
);
const previewTemplate = computed(() => {
  const withMedia = templateWithMediaPreview(
    props.template,
    currentMediaUrl.value
  );
  return templateWithHeaderPreview(withMedia, processedParams.value.header);
});
const variables = computed(() => previewVariables(processedParams.value));

const uploadMedia = async file => {
  try {
    await prepareMedia(file);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CHANNEL_TEMPLATE_PRESETS.UPLOAD_ERROR')
    );
  }
};

const send = payload => emit('sendMessage', payload);
const goBack = () => emit('back');

onMounted(async () => {
  fallbackToNative.value = !(await initializePreset());
});
</script>

<template>
  <NativeWhatsappTemplate
    v-if="fallbackToNative"
    :template="template"
    @send-message="send"
    @back="goBack"
  />

  <div v-else class="flex w-[min(46rem,calc(100vw-2rem))] flex-col">
    <header class="shrink-0 border-b border-n-weak px-4 py-4">
      <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 class="text-sm font-semibold text-n-slate-12">
          {{ template.name }}
        </h2>
        <span
          class="rounded-md px-2 py-0.5 text-xs font-medium"
          :class="statusTone"
        >
          {{
            loadingPreset
              ? t('CHANNEL_TEMPLATE_PRESETS.LOADING_PRESET')
              : statusLabel
          }}
        </span>
      </div>
      <p v-if="!loadingPreset" class="mt-1 text-xs text-n-slate-11">
        {{ statusMessage }}
      </p>
    </header>

    <div
      class="grid max-h-[70vh] items-start gap-5 overflow-y-auto p-4 lg:grid-cols-[minmax(0,7fr)_minmax(16rem,5fr)]"
    >
      <div class="min-w-0">
        <TemplateSendFields
          :template="template"
          :processed-params="processedParams"
          :media-source="mediaSource"
          :uploaded-media-name="uploadedMediaName"
          :uploading-media="uploadingMedia"
          :has-error="showValidationError"
          @upload-media="uploadMedia"
        />

        <WhatsAppTemplateParser
          ref="parser"
          headless
          :template="template"
          @send-message="send"
          @back="goBack"
        >
          <template #actions="{ sendMessage, goBack: parserGoBack, disabled }">
            <div
              class="mt-5 flex flex-col-reverse gap-3 border-t border-n-weak pt-4 sm:flex-row sm:justify-end"
            >
              <Button
                :label="
                  t(
                    'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.BACK'
                  )
                "
                color="slate"
                variant="faded"
                class="font-medium"
                @click="parserGoBack"
              />
              <Button
                :label="
                  t(
                    'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.SEND_MESSAGE'
                  )
                "
                class="font-medium"
                :disabled="
                  disabled ||
                  hasEmptyHeaderText ||
                  loadingPreset ||
                  uploadingMedia
                "
                @click="sendMessage"
              />
            </div>
          </template>
        </WhatsAppTemplateParser>
      </div>

      <aside class="min-w-0 rounded-xl border border-n-weak bg-n-alpha-1 p-4">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('CHANNEL_TEMPLATE_PRESETS.PREVIEW') }}
        </h3>
        <div class="mt-4 flex justify-center">
          <TemplatePreview
            :template="previewTemplate"
            :variables="variables"
            :platform="PLATFORMS.WHATSAPP"
          />
        </div>
      </aside>
    </div>
  </div>
</template>
